/* The conversation's two stages: board N's hero (the mark, the title and the composer under it)
 * and board M's thread over the docked composer. The newest message stands at the bottom: the
 * scroller is `column-reverse`, so a reply growing below keeps the view pinned to it and the older
 * turns scroll up out of the way. */

.heroStage {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  min-height: 0;
  padding: var(--space-0) var(--space-6);
  overflow-y: auto;
}

/* The start stands above the middle at any height (board P5f): three parts of the free room above
 * it, seven below, so the headline sits near 28% of the window and the composer near 39%. */
.heroStage::before,
.heroStage::after {
  flex: 3 1 16px;
  content: '';
}

.heroStage::after {
  flex-grow: 7;
}

.hero {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-4);
  align-items: center;
}

/* The hero figure (board P5e, variant D): 88 px, its light driven by `useHeroLight`. */
.heroMark {
  width: 88px;
  height: 88px;
}

/* The Pulpit greeting's style (TrainerHomeScreen `.greeting`): 34/40, bold, tracked in. */
.heroTitle {
  margin: var(--space-0);
  font-size: var(--fs-home-title);
  font-weight: var(--fw-bold);
  line-height: var(--lh-home-name);
  letter-spacing: var(--ls-title);
  text-align: center;
}

.heroDock {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-3);
  width: 100%;
  max-width: 680px;
  margin-top: var(--space-8);
}

/* „Przykłady": three quiet text cards in a row under the composer, on the empty start only. A press
 * puts the request in the composer; the trainer sends it. */
.examples {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-10px);
  width: 100%;
  max-width: 680px;
  margin-top: var(--space-7);
}

.examplesLabel {
  margin: var(--space-0);
  padding-left: var(--space-2px);
  font-size: var(--fs-label);
  line-height: var(--lh-home-label);
  color: var(--text-2);
}

.exampleList {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-2);
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.exampleItem {
  display: flex;
  min-width: 0;
}

.example {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
  padding: var(--space-3) var(--space-14px) var(--space-13px);
  font-family: var(--font-body);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  transition-duration: var(--dur-fast);
  transition-property: background-color, border-color;
  transition-timing-function: var(--ease);
}

.example:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.example:hover:not([aria-disabled='true']) {
  background: var(--hover-tint);
  border-color: var(--border-2);
}

.example:active:not([aria-disabled='true']) {
  background: var(--bg-chip);
}

.example[aria-disabled='true'] {
  cursor: default;
  opacity: 0.4;
}

.exampleTitle {
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  line-height: var(--lh-home-body);
}

.exampleLine {
  font-size: var(--fs-foot);
  line-height: var(--lh-home-foot);
  color: var(--text-2);
}

.threadStage {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  min-height: 0;
}

.scroller {
  display: flex;
  flex: 1;
  flex-direction: column-reverse;
  align-self: stretch;
  min-height: 0;
  overflow-y: auto;
}

/* The thread and the dock share one 680px column (board P5d-3), each with a 16px gutter of its
 * own, so beside the session panel at 1280 neither touches its neighbours. */
.thread {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  width: 100%;
  max-width: 712px;
  margin: var(--space-0) auto;
  padding: var(--space-6) var(--space-4) var(--space-2);
}

.dock {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-3);
  width: 100%;
  max-width: 712px;
  padding: var(--space-2) var(--space-4) var(--space-5);
}

.bad {
  color: var(--danger-ink);
}

/* The builder's assistant (boards I, S): the thread fills the panel, newest at the bottom. */
.panelStage {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.panelThread {
  display: flex;
  flex-direction: column;
  gap: var(--space-18px);
  padding: var(--space-18px);
}

.panelDock {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-10px);
  padding: var(--space-14px) var(--space-18px) var(--space-18px);
  border-top: var(--border-hairline) solid var(--border);
}

/* „Uwaga do: …" over the composer: the change the next message rejects (R16), removable. */
.rejecting {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  margin: var(--space-0) var(--space-14px);
  font-size: var(--fs-note);
  font-weight: var(--fw-regular);
  color: var(--text-2);
}

.rejectingText {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.rejectingClear {
  display: grid;
  flex: none;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: var(--space-0);
  color: var(--text-2);
  cursor: pointer;
  background: none;
  border: 0;
  border-radius: var(--radius-xs);
}

.rejectingClear:hover {
  color: var(--text);
}

.rejectingClear:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.rejectingGlyph {
  width: 12px;
  height: 12px;
}
